import { Trans } from '@lingui/react/macro';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import type { billing_DunningSettingsSnapshotResponse } from '@/api/models/billing_DunningSettingsSnapshotResponse';
import { DunningSweeperBanner } from './DunningStatusBanners';

interface Props {
  snapshot: billing_DunningSettingsSnapshotResponse | undefined;
}

export function DunningSettingsSnapshot({ snapshot }: Props) {
  if (!snapshot) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="dunning-settings-snapshot-empty">
        <Trans>Settings snapshot unavailable.</Trans>
      </p>
    );
  }

  return (
    <div data-testid="dunning-settings-snapshot" className="space-y-2">
      {/* "Master enabled: Yes" below says nothing about whether a reminder can leave. */}
      <DunningSweeperBanner />
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          <Trans>Show settings</Trans>
        </summary>
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              <Trans>Master enabled</Trans>:
            </span>
            <Badge variant={snapshot.enabled ? 'default' : 'outline'}>
              {snapshot.enabled ? <Trans>Yes</Trans> : <Trans>No</Trans>}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <div>
              <Trans>Sweep hour (local)</Trans>: <span className="font-mono">{snapshot.sweep_hour_local ?? '—'}</span>
            </div>
            <div>
              <Trans>Dust threshold (cents)</Trans>:{' '}
              <span className="font-mono">{snapshot.dust_threshold_cents ?? '—'}</span>
            </div>
            <div>
              <Trans>Min reminder gap (days)</Trans>:{' '}
              <span className="font-mono">{snapshot.min_reminder_gap_days ?? '—'}</span>
            </div>
          </div>
          {snapshot.stages && snapshot.stages.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold mt-2 mb-1">
                <Trans>Stages</Trans>
              </h4>
              <ul className="text-xs space-y-1">
                {snapshot.stages.map((s) => (
                  <li key={s.stage} className="flex items-center gap-2">
                    <span className="font-mono w-32">{s.stage}</span>
                    <Badge variant={s.enabled ? 'default' : 'outline'}>
                      {s.enabled ? <Trans>enabled</Trans> : <Trans>disabled</Trans>}
                    </Badge>
                    {s.offset_days !== undefined && (
                      <span className="text-muted-foreground">+{s.offset_days}d</span>
                    )}
                    {s.after_final && <span className="text-muted-foreground">→ {s.after_final}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-2">
            <Link to="/settings/dunning" className="text-primary hover:underline text-xs">
              <Trans>Configure dunning settings</Trans>
            </Link>
          </div>
        </div>
      </details>
    </div>
  );
}
